/**
 * Transform Builder Store (Zustand)
 *
 * Manages transform DAG editor state: nodes, edges, catalog, save/load.
 */

import { create } from 'zustand';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { edenError } from '@solidafy/api-client';
import { generateDsl, generateDslToNode, validateGraph } from '@/lib/transform-dsl';
import { getNodeSpec } from '@/lib/transform-nodes';
import type { TransformNode, TransformEdge, CatalogTableDetail, NodeResult } from '@/types/transform';

interface TransformBuilderState {
  // Metadata
  transformId: string | null;
  transformName: string;
  transformDescription: string;

  // Graph
  nodes: TransformNode[];
  edges: TransformEdge[];
  selectedNodeId: string | null;

  // Canvas
  zoom: number;
  panX: number;
  panY: number;

  // Schema
  catalogTables: CatalogTableDetail[];
  catalogLoading: boolean;

  // Results
  nodeResults: Map<string, NodeResult>;

  // Preview
  previewNodeId: string | null;
  previewLoading: boolean;
  previewData: {
    columns: Array<{ name: string; type: string }>;
    rows: unknown[];
    rowCount: number;
    error?: string;
    durationMs?: number;
  } | null;
  showPreviewModal: boolean;

  // UI
  isDirty: boolean;
  isSaving: boolean;
  showNodePicker: boolean;
  nodePickerScreenPosition: { x: number; y: number } | null;
  nodePickerCanvasPosition: { x: number; y: number } | null;
  nodePickerSourceNodeId: string | null;
  connectingFrom: { nodeId: string; port: string } | null;
  showDslPreview: boolean;

  // Computed helpers
  getColumnsAtNode: (nodeId: string) => Array<{ name: string; type: string }>;
  getInputColumnsForNode: (nodeId: string) => Array<{ name: string; type: string }>;
  getGeneratedDsl: () => string;
  getValidationErrors: () => string[];

  // Actions
  loadTransform: (id: string) => Promise<boolean>;
  saveTransform: () => Promise<boolean>;
  loadCatalogTableDetails: () => Promise<void>;

  addNode: (type: string, position: { x: number; y: number }, config?: Record<string, unknown>) => TransformNode;
  addNodeWithConnection: (type: string, position: { x: number; y: number }, sourceNodeId?: string) => TransformNode;
  removeNode: (id: string) => void;
  moveNode: (id: string, pos: { x: number; y: number }) => void;
  updateNodeConfig: (id: string, config: Record<string, unknown>) => void;
  addEdge: (src: string, srcPort: string, tgt: string, tgtPort: string) => TransformEdge | null;
  removeEdge: (id: string) => void;
  selectNode: (id: string | null) => void;
  openNodePicker: (
    position: { screen: { x: number; y: number }; canvas: { x: number; y: number } },
    sourceNodeId?: string,
  ) => void;
  closeNodePicker: () => void;
  startConnection: (nodeId: string, port: string) => void;
  endConnection: (targetNodeId: string, targetPort: string) => void;
  cancelConnection: () => void;
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;
  previewNode: (nodeId: string) => Promise<void>;
  closePreview: () => void;
  resetStore: () => void;
}

function generateId(): string {
  return `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function wouldCreateCycle(edges: TransformEdge[], sourceId: string, targetId: string): boolean {
  const visited = new Set<string>();
  const outgoing = new Map<string, string[]>();

  edges.forEach((e) => {
    if (!outgoing.has(e.sourceId)) {
      outgoing.set(e.sourceId, []);
    }
    outgoing.get(e.sourceId)!.push(e.targetId);
  });

  if (!outgoing.has(sourceId)) {
    outgoing.set(sourceId, []);
  }
  outgoing.get(sourceId)!.push(targetId);

  function dfs(nodeId: string): boolean {
    if (nodeId === sourceId) {
      return true;
    }
    if (visited.has(nodeId)) {
      return false;
    }
    visited.add(nodeId);
    return (outgoing.get(nodeId) || []).some((n) => dfs(n));
  }

  return dfs(targetId);
}

export const useTransformBuilderStore = create<TransformBuilderState>((set, get) => ({
  transformId: null,
  transformName: 'Untitled Transform',
  transformDescription: '',

  nodes: [],
  edges: [],
  selectedNodeId: null,

  zoom: 1,
  panX: 0,
  panY: 0,

  catalogTables: [],
  catalogLoading: false,

  nodeResults: new Map(),

  previewNodeId: null,
  previewLoading: false,
  previewData: null,
  showPreviewModal: false,

  isDirty: false,
  isSaving: false,
  showNodePicker: false,
  nodePickerScreenPosition: null,
  nodePickerCanvasPosition: null,
  nodePickerSourceNodeId: null,
  connectingFrom: null,
  showDslPreview: false,

  // ── Column resolution (same as Vue getColumnsAtNode) ──
  getColumnsAtNode: (nodeId: string) => {
    const { nodes, edges, catalogTables } = get();
    return resolveColumnsAtNode(nodeId, nodes, edges, catalogTables);
  },

  getInputColumnsForNode: (nodeId: string) => {
    const { edges } = get();
    const incomingEdges = edges.filter((e) => e.targetId === nodeId);
    if (incomingEdges.length === 0) {
      return [];
    }

    const allColumns: Array<{ name: string; type: string }> = [];
    const seenNames = new Set<string>();
    for (const edge of incomingEdges) {
      const parentColumns = get().getColumnsAtNode(edge.sourceId);
      for (const col of parentColumns) {
        if (!seenNames.has(col.name)) {
          seenNames.add(col.name);
          allColumns.push(col);
        }
      }
    }
    return allColumns;
  },

  getGeneratedDsl: () => {
    const { nodes, edges } = get();
    return generateDsl(nodes, edges);
  },

  getValidationErrors: () => {
    const { nodes, edges } = get();
    return validateGraph(nodes, edges);
  },

  // ── Load transform ──
  loadTransform: async (id: string) => {
    try {
      const { data, error } = await api.api.v1.transforms({ id }).get();
      if (error) {
        throw new Error(edenError(error, 'Failed to load transform'));
      }
      set({
        transformId: data.id,
        transformName: data.name || 'Untitled Transform',
        transformDescription: data.description || '',
        nodes: (data.nodes || []) as TransformNode[],
        edges: (data.edges || []) as TransformEdge[],
        isDirty: false,
      });
      return true;
    } catch {
      toast.error('Failed to load transform');
      return false;
    }
  },

  // ── Save transform ──
  saveTransform: async () => {
    const s = get();
    set({ isSaving: true });
    try {
      const updatePayload = {
        name: s.transformName,
        description: s.transformDescription,
        nodes: s.nodes as unknown[],
        edges: s.edges as unknown[],
        generated_dsl: generateDsl(s.nodes, s.edges),
      };

      if (s.transformId) {
        const { error } = await api.api.v1.transforms({ id: s.transformId }).put(updatePayload);
        if (error) {
          throw new Error(edenError(error, 'Failed to update transform'));
        }
      } else {
        const { data, error } = await api.api.v1.transforms.post({
          name: s.transformName,
          description: s.transformDescription,
        });
        if (error || !data || !('id' in data)) {
          throw new Error(edenError(error!, 'Failed to create transform'));
        }
        set({ transformId: data.id });
        // Save nodes/edges/dsl to the newly created transform
        const { error: updateError } = await api.api.v1.transforms({ id: data.id }).put(updatePayload);
        if (updateError) {
          throw new Error(edenError(updateError, 'Failed to save transform data'));
        }
      }
      set({ isDirty: false, isSaving: false });
      return true;
    } catch {
      toast.error('Failed to save transform');
      set({ isSaving: false });
      return false;
    }
  },

  // ── Load catalog ──
  loadCatalogTableDetails: async () => {
    set({ catalogLoading: true });
    try {
      const { data, error } = await api.api.v1.transforms.tables.get();
      if (error) {
        throw new Error(edenError(error, 'Failed to load catalog tables'));
      }
      const tables: CatalogTableDetail[] = Object.entries(data.namespaces).flatMap(([ns, nsTables]) =>
        nsTables.map((t) => ({
          namespace: ns,
          table: t.name,
          fullName: t.full_name,
          columns: t.columns.map((col) => ({
            name: col.name,
            type: col.type || 'string',
          })),
        })),
      );
      set({ catalogTables: tables, catalogLoading: false });
    } catch {
      set({ catalogLoading: false });
    }
  },

  // ── Node management ──
  addNode: (type, position, config = {}) => {
    const node: TransformNode = {
      id: generateId(),
      type,
      position,
      config: { ...config },
      configured: false,
    };

    if (type === 'source' && !config.variable) {
      const existingVars = get()
        .nodes.filter((n) => n.type === 'source')
        .map((n) => (n.config?.variable as string) || '');
      let varName = 'src';
      let counter = 1;
      while (existingVars.includes(varName)) {
        varName = `src${counter++}`;
      }
      node.config.variable = varName;
    }

    set((state) => ({
      nodes: [...state.nodes, node],
      selectedNodeId: node.id,
      isDirty: true,
    }));
    return node;
  },

  addNodeWithConnection: (type, position, sourceNodeId) => {
    const node = get().addNode(type, position);
    if (sourceNodeId) {
      get().addEdge(sourceNodeId, 'output', node.id, 'input');
    }
    return node;
  },

  removeNode: (id) =>
    set((state) => ({
      nodes: state.nodes.filter((n) => n.id !== id),
      edges: state.edges.filter((e) => e.sourceId !== id && e.targetId !== id),
      selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
      isDirty: true,
    })),

  moveNode: (id, pos) =>
    set((state) => ({
      nodes: state.nodes.map((n) => (n.id === id ? { ...n, position: pos } : n)),
      isDirty: true,
    })),

  updateNodeConfig: (id, config) =>
    set((state) => {
      const newNodes = state.nodes.map((n) => {
        if (n.id !== id) {
          return n;
        }
        const merged = { ...n.config, ...config };
        const spec = getNodeSpec(n.type);
        let configured = false;
        if (spec) {
          const requiredFields = spec.fields.filter((f) => f.required);
          configured = requiredFields.every((f) => {
            const v = merged[f.name];
            return v !== undefined && v !== null && v !== '';
          });
        }
        return { ...n, config: merged, configured };
      });
      return { nodes: newNodes, isDirty: true };
    }),

  addEdge: (src, srcPort, tgt, tgtPort) => {
    const { nodes, edges } = get();
    const sourceNode = nodes.find((n) => n.id === src);
    const targetNode = nodes.find((n) => n.id === tgt);
    if (!sourceNode || !targetNode) {
      return null;
    }

    const targetSpec = getNodeSpec(targetNode.type);
    if (!targetSpec) {
      return null;
    }

    if (wouldCreateCycle(edges, src, tgt)) {
      return null;
    }

    const existingInputs = edges.filter((e) => e.targetId === tgt);
    let newEdges = edges;
    if (existingInputs.length >= targetSpec.inputs) {
      if (targetSpec.inputs === 1) {
        newEdges = edges.filter((e) => e.targetId !== tgt);
      } else {
        return null;
      }
    }

    let resolvedPort = tgtPort;
    if (targetSpec.inputs > 1) {
      const usedPorts = new Set(existingInputs.map((e) => e.targetPort));
      if (!usedPorts.has('input')) {
        resolvedPort = 'input';
      } else if (!usedPorts.has('input2')) {
        resolvedPort = 'input2';
      }
    }

    const edge: TransformEdge = {
      id: `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sourceId: src,
      sourcePort: srcPort,
      targetId: tgt,
      targetPort: resolvedPort,
    };

    set({ edges: [...newEdges, edge], isDirty: true });
    return edge;
  },

  removeEdge: (id) =>
    set((state) => ({
      edges: state.edges.filter((e) => e.id !== id),
      isDirty: true,
    })),

  selectNode: (id) => set({ selectedNodeId: id }),

  openNodePicker: (position, sourceNodeId) =>
    set({
      nodePickerScreenPosition: position.screen,
      nodePickerCanvasPosition: position.canvas,
      nodePickerSourceNodeId: sourceNodeId || null,
      showNodePicker: true,
    }),

  closeNodePicker: () =>
    set({
      showNodePicker: false,
      nodePickerScreenPosition: null,
      nodePickerCanvasPosition: null,
      nodePickerSourceNodeId: null,
    }),

  startConnection: (nodeId, port) => set({ connectingFrom: { nodeId, port } }),
  endConnection: (targetNodeId, targetPort) => {
    const conn = get().connectingFrom;
    if (conn) {
      get().addEdge(conn.nodeId, conn.port, targetNodeId, targetPort);
    }
    set({ connectingFrom: null });
  },
  cancelConnection: () => set({ connectingFrom: null }),

  setZoom: (zoom) => set({ zoom: Math.max(0.25, Math.min(2, zoom)) }),
  setPan: (x, y) => set({ panX: x, panY: y }),

  // ── Preview ──
  previewNode: async (nodeId: string) => {
    set({ previewNodeId: nodeId, previewLoading: true, previewData: null, showPreviewModal: true });
    try {
      const { nodes, edges } = get();
      const dsl = generateDslToNode(nodes, edges, nodeId);
      if (!dsl || dsl.startsWith('//')) {
        set({
          previewData: { columns: [], rows: [], rowCount: 0, error: 'No valid DSL generated.' },
          previewLoading: false,
        });
        return;
      }
      const { data, error } = await api.api.v1.transforms.preview.post({ dsl, limit: 10 });
      if (error) {
        throw new Error(edenError(error, 'Preview failed'));
      }
      set({
        previewData: {
          columns: data.columns,
          rows: data.rows,
          rowCount: data.row_count,
          error: data.error,
          durationMs: data.duration_ms,
        },
        previewLoading: false,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Preview failed';
      set({ previewData: { columns: [], rows: [], rowCount: 0, error: message }, previewLoading: false });
    }
  },

  closePreview: () => set({ showPreviewModal: false, previewNodeId: null, previewData: null }),

  resetStore: () =>
    set({
      transformId: null,
      transformName: 'Untitled Transform',
      transformDescription: '',
      nodes: [],
      edges: [],
      selectedNodeId: null,
      zoom: 1,
      panX: 0,
      panY: 0,
      nodeResults: new Map(),
      isDirty: false,
      isSaving: false,
      showNodePicker: false,
      connectingFrom: null,
      showDslPreview: false,
    }),
}));

// ── Column resolution (extracted for reuse by useLocalTransformState) ──

export function resolveColumnsAtNode(
  nodeId: string,
  nodes: TransformNode[],
  edges: TransformEdge[],
  catalogTables: CatalogTableDetail[],
): Array<{ name: string; type: string }> {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return [];
  }

  if (node.type === 'source') {
    const table = catalogTables.find((t) => t.fullName === node.config?.table);
    return table?.columns || [];
  }

  const incomingEdges = edges.filter((e) => e.targetId === nodeId);
  if (incomingEdges.length === 0) {
    return [];
  }

  const parentNode = nodes.find((n) => n.id === incomingEdges[0].sourceId);
  if (!parentNode) {
    return [];
  }

  const parentColumns = resolveColumnsAtNode(parentNode.id, nodes, edges, catalogTables);

  switch (parentNode.type) {
    case 'select': {
      const selectedCols = (parentNode.config?.columns || []) as string[];
      return parentColumns.filter((c) => selectedCols.includes(c.name));
    }
    case 'drop': {
      const droppedCols = (parentNode.config?.columns || []) as string[];
      return parentColumns.filter((c) => !droppedCols.includes(c.name));
    }
    case 'rename': {
      const oldName = parentNode.config?.oldName as string;
      const newName = parentNode.config?.newName as string;
      if (oldName && newName) {
        return parentColumns.map((c) => (c.name === oldName ? { ...c, name: newName } : c));
      }
      return parentColumns;
    }
    case 'add': {
      const colName = parentNode.config?.name as string;
      const colType = (parentNode.config?.type as string) || 'string';
      if (colName) {
        return [...parentColumns, { name: colName, type: colType }];
      }
      return parentColumns;
    }
    case 'cast': {
      const colName = parentNode.config?.column as string;
      const newType = parentNode.config?.toType as string;
      if (colName && newType) {
        return parentColumns.map((c) => (c.name === colName ? { ...c, type: newType } : c));
      }
      return parentColumns;
    }
    case 'join': {
      const leftEdge = incomingEdges.find((e) => e.targetPort === 'input');
      const rightEdge = incomingEdges.find((e) => e.targetPort === 'input2');
      const leftParent = leftEdge ? nodes.find((n) => n.id === leftEdge.sourceId) : null;
      const rightParent = rightEdge ? nodes.find((n) => n.id === rightEdge.sourceId) : null;
      const leftColumns = leftParent ? resolveColumnsAtNode(leftParent.id, nodes, edges, catalogTables) : parentColumns;
      const rightColumns = rightParent ? resolveColumnsAtNode(rightParent.id, nodes, edges, catalogTables) : [];
      const existingNames = new Set(leftColumns.map((c) => c.name));
      return [...leftColumns, ...rightColumns.filter((c) => !existingNames.has(c.name))];
    }
    case 'group_by': {
      const groupCols = (parentNode.config?.columns || []) as string[];
      const aggCols = (parentNode.config?.aggregations || []) as Array<{ alias?: string }>;
      const resultCols = groupCols.map((name) => {
        const orig = parentColumns.find((c) => c.name === name);
        return orig || { name, type: 'string' };
      });
      aggCols.forEach((agg) => {
        if (agg.alias) {
          resultCols.push({ name: agg.alias, type: 'number' });
        }
      });
      return resultCols;
    }
    default:
      return parentColumns;
  }
}
